import { VoiceDetailsCard } from "@/components/atoms/interactionDetailsCard";
import { InteractionActionCard } from "@/components/molecules/interactionActionCard";
import {
	acceptSimultaneouslyOfferedCall,
	rejectSimultaneouslyOfferedCall,
} from "@/repository/queryFns";
import { Call20Regular } from "@fluentui/react-icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import {
	CallSessionActions,
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import PlaySoundHelper, { SoundType } from "@/common/helpers/play-sound-helper";
import { AgentInteractionStatus } from "@/common/constants/callSession";
import {
	getRingingFilePath,
	ringingFilePathQueryKey,
} from "@/repository/queryFns/utils";
import { AcceptSOInteractionResponse } from "@/repository/models/simultaneousOffering";
import { useMessage } from "@/common/messages";
import { captureError } from "@/common/helpers/sentryHelper";
import { useAcs } from "@/common/acs/AcsContext";

export const VoiceSimultaneousCard = () => {
	const intl = useIntl();
	const [displayCards, setDisplayCards] = useState<boolean>(false);
	const [audioFile, setAudioFile] = useState<HTMLAudioElement | undefined>(
		undefined
	);

	const { show: showMessage } = useMessage();

	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const updateCallSession = useCallSessionStore(
		(actions: CallSessionActions) => actions.update
	);

	const { incomingCall, ongoingCalls } = useAcs();

	const hiddenStatus: AgentInteractionStatus[] = [
		AgentInteractionStatus.Terminated,
		AgentInteractionStatus.SimultaneousOfferingTerminated,
		AgentInteractionStatus.SimultaneousOfferingCallerLeft,
		AgentInteractionStatus.SimultaneousOfferingIgnored,
		AgentInteractionStatus.AcceptedWarmTransferInvitation,
		AgentInteractionStatus.CallbackRegistered,
	];

	const { data: ringingFilePath } = useQuery<string>({
		queryKey: [ringingFilePathQueryKey, true],
		queryFn: () => getRingingFilePath(true),
	});

	useEffect(() => {
		if (displayCards) {
			if (ringingFilePath) {
				let offeringAudioFile = PlaySoundHelper.playSound(
					SoundType.Ringing,
					ringingFilePath,
					true
				);

				if (offeringAudioFile) {
					setAudioFile(offeringAudioFile);
				}
			}
		} else {
			if (audioFile) {
				audioFile.pause();
			}
		}
	}, [displayCards, ringingFilePath]);

	useEffect(() => {
		if (incomingCall || (ongoingCalls?.size ?? 0) > 0) {
			return; // don't display SO card if there's an unify call
		}

		if (!callSession) {
			if (displayCards) {
				stopOfferingInteraction();
			}

			return;
		}

		if (
			!callSession.isSimultaneousOffering ||
			hiddenStatus.includes(callSession.state!)
		) {
			stopOfferingInteraction();

			return;
		} else {
			setDisplayCards(true);
		}
	}, [callSession]);

	useEffect(() => {
		return () => {
			stopOfferingInteraction();
		}
	}, []);

	const rejectSimultaneouslyOfferedCallMutation = useMutation({
		mutationFn: (messagingId: string) =>
			rejectSimultaneouslyOfferedCall(messagingId),
		onSuccess: (_: any) => {
			updateCallSession(null);
		},
		onError: (err: any, data: any, _context: any) => {
			captureError(
				err,
				"Error rejecting simultaneously offered call",
				undefined,
				data
			);
			console.error(err);
		},
	});

	const rejectInteraction = useCallback(() => {
		stopOfferingInteraction();

		if (!!callSession?.id) {
			PlaySoundHelper.stopPlayingLoop();
			rejectSimultaneouslyOfferedCallMutation.mutate(callSession.id);
		}
	}, [callSession]);

	const acceptSimultaneouslyOfferedCallMutation = useMutation({
		mutationFn: (callSessionId: string) =>
			acceptSimultaneouslyOfferedCall(callSessionId),
		onSuccess: (response: AcceptSOInteractionResponse) => {
			switch (response) {
				case AcceptSOInteractionResponse.AlreadyAcceptedByAnotherAgent:
					showMessage("global", {
						intent: "error",
						text: intl.formatMessage({
							id: "Interaction.Messages.CallAcceptedByAnotherAgent",
						}),
					});
					break;
				case AcceptSOInteractionResponse.Error:
				case AcceptSOInteractionResponse.InteractionNotFound:
					showMessage("global", {
						intent: "error",
						text: intl.formatMessage({
							id: "ErrorMessage.SONotFound",
						}),
					});
					break;
			}

			stopOfferingInteraction();
		},
		onError: (err, data) => {
			captureError(
				err,
				"Error accepting simultaneously offered call",
				undefined,
				{ callSessionId: data }
			);
			console.error(err);
		},
	});

	const acceptInteraction = useCallback(() => {
		if (!!callSession?.id) {
			acceptSimultaneouslyOfferedCallMutation.mutate(callSession.id);
		}
	}, [callSession]);

	const stopOfferingInteraction = () => {
		PlaySoundHelper.stopPlayingLoop();
		setDisplayCards(false);
	};

	return (
		displayCards && (
			<>
				<VoiceDetailsCard
					contactName={callSession?.caller?.name}
					contactInfo={callSession?.caller?.contactInformation}
					queueName={callSession?.queueName}
					skills={callSession?.skills}
					isNextToInternalToaster={true}
					offeringVariableText={callSession?.offeringVariableText}
					offeringVariableTextIsAiGenerated={callSession?.offeringVariableTextIsAiGenerated}
				/>
				<InteractionActionCard
					contactName={callSession?.caller?.name}
					icon={<Call20Regular />}
					message={intl.formatMessage({
						id: "Interaction.Contents.CallMessage",
					})}
					acceptInteraction={acceptInteraction}
					rejectInteraction={rejectInteraction}
				/>
			</>
		)
	);
};
